/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { BACK_CLICK_WINDOW_MS } from './constants';

const logger = Logger.getInstance();

const DEFAULT_MIN_AWAY_MS = 500;

/**
 * Detects an "accidental click" by watching for the user re-engaging with the
 * publisher page shortly after an ad interaction was counted. The premise: if a
 * user clicks an ad, leaves (a new tab/window opens), realises the mistake and
 * comes straight back to interact with the original page, the click was likely
 * accidental.
 *
 * This class owns only the signal bookkeeping and timing; it has no knowledge of
 * placements. Both Placement and AdvertorialPlacement compose an instance and
 * supply their own dispatch callback, so the detection logic lives in one place.
 *
 * Model: on arm() the detector records the arm time and watches for the first
 * re-engagement signal in the window [minAwayMs, maxAwayMs]. Any one of these
 * fires the callback:
 *   - a `click` on the document (device-independent: covers mouse click, touch
 *     tap and keyboard activation)
 *   - a `scroll` (registered passive)
 *   - a `visibilitychange` returning the page to visible (tab-switch / minimise
 *     return, where the user comes back without clicking or scrolling)
 *   - a `pageshow` (bfcache restore / back-button)
 *
 * Signals arriving < minAwayMs after arming are ignored as browser
 * auto-dispatched side effects of the navigation (a synthetic click/scroll/etc.
 * the browser emits right after the ad click) — too fast to be a human return.
 * Critically, a sub-floor signal is ignored WITHOUT disarming, and the listeners
 * are NOT registered with `{ once }`, so the listener survives to catch the
 * genuine later return. One-shot semantics come from the internal fired/armed
 * guards, not from `{ once }`.
 *
 * The value passed to the callback is the elapsed time since arming, i.e. the
 * gap between the counted interaction and re-engagement.
 *
 * `window` focus/blur are deliberately NOT used: on desktop, closing a child tab
 * does not reliably fire `focus` on the opener window, so a focus-based return
 * signal misses genuine accidental clicks (false negatives). The signals above
 * fire on the actions the user actually takes when they return.
 *
 * The callback fires at most once, after which the detector self-disarms. A
 * ceiling timer disarms the detector if no signal arrives within maxAwayMs.
 */
class AccidentalClickDetector {
  private armed: boolean = false;
  private fired: boolean = false;
  private armedAt: number | null = null;
  private onAccidental: ((awayDurationMs: number) => void) | null = null;
  private ceilingTimerId: ReturnType<typeof setTimeout> | null = null;
  private registerTimerId: ReturnType<typeof setTimeout> | null = null;

  // Bound once so add/removeEventListener reference identical handlers.
  private readonly clickHandler = (): void => this.handleReEngagement('click');
  private readonly scrollHandler = (): void => this.handleReEngagement('scroll');
  private readonly visibilityHandler = (): void => this.handleVisibilityChange();
  private readonly pageshowHandler = (): void => this.handleReEngagement('pageshow');

  constructor(
    private readonly maxAwayMs: number = BACK_CLICK_WINDOW_MS,
    private readonly minAwayMs: number = DEFAULT_MIN_AWAY_MS,
  ) {}

  /**
   * Begin watching for re-engagement. Call once immediately after an interaction
   * has been counted. Re-arming an already-armed detector is a no-op.
   * onAccidental receives the elapsed time since arming (ms) and fires at most
   * once.
   */
  public arm(onAccidental: (awayDurationMs: number) => void): void {
    if (this.armed || this.fired) {
      return;
    }
    this.armed = true;
    this.onAccidental = onAccidental;
    this.armedAt = Date.now();

    // No signal within the window => the user engaged with the destination (or
    // simply moved on); disarm so a much-later signal cannot be miscounted.
    this.ceilingTimerId = setTimeout(() => {
      logger.debug('AccidentalClickDetector: window elapsed with no signal, disarming');
      this.disarm();
    }, this.maxAwayMs);

    // Register on the next tick, not synchronously. arm() is called from inside
    // the very click handler that counted the interaction; that click is still
    // propagating and would otherwise bubble straight into our document click
    // listener and self-trigger. Deferring registration until the current event
    // has finished guarantees only the user's NEXT gesture counts.
    this.registerTimerId = setTimeout(() => {
      this.registerTimerId = null;
      if (!this.armed) {
        return;
      }
      // Not registered with { once }: a sub-floor auto-dispatched signal must be
      // ignored WITHOUT the browser having already removed the listener, so the
      // genuine later return is still caught. disarm() removes all of these.
      document.addEventListener('click', this.clickHandler);
      document.addEventListener('scroll', this.scrollHandler, { passive: true });
      document.addEventListener('visibilitychange', this.visibilityHandler);
      // pageshow fires on bfcache restore (back-button) where visibilitychange
      // can be unreliable (notably iOS Safari). Any pageshow while armed means
      // the user is back on our page.
      window.addEventListener('pageshow', this.pageshowHandler);
    }, 0);
  }

  /**
   * Stop watching and release all listeners and timers. Idempotent; safe to call
   * from a placement's destroy() regardless of armed state.
   */
  public disarm(): void {
    if (!this.armed) {
      return;
    }
    this.armed = false;
    this.onAccidental = null;
    this.armedAt = null;

    if (this.ceilingTimerId !== null) {
      clearTimeout(this.ceilingTimerId);
      this.ceilingTimerId = null;
    }

    if (this.registerTimerId !== null) {
      clearTimeout(this.registerTimerId);
      this.registerTimerId = null;
    }

    // removeEventListener with the same reference is a no-op for a { once }
    // listener that already fired, and cleanly removes one that never did.
    document.removeEventListener('click', this.clickHandler);
    document.removeEventListener('scroll', this.scrollHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('pageshow', this.pageshowHandler);
  }

  /**
   * Release everything and clear the fired latch so the detector can be armed
   * again. The fired latch makes a single arming one-shot; reset() lifts it for
   * a new detection window (e.g. a placement serving a fresh opportunity). Safe
   * to call whether the detector is armed, already fired, or idle.
   */
  public reset(): void {
    this.disarm();
    this.fired = false;
  }

  private handleVisibilityChange(): void {
    // Only the return to visible is a re-engagement signal; the hidden
    // transition is ignored (we measure from arm time, not from a leave).
    if (document.visibilityState === 'visible') {
      this.handleReEngagement('visibilitychange');
    }
  }

  /**
   * A re-engagement signal arrived. If it is within the window [minAwayMs,
   * maxAwayMs], fire the callback once with the elapsed time since arming. First
   * signal wins: firing disarms and removes all listeners, so any later signal is
   * a no-op.
   */
  private handleReEngagement(signal: string): void {
    if (!this.armed || this.armedAt === null) {
      return;
    }

    const elapsedMs = Date.now() - this.armedAt;

    if (elapsedMs > this.maxAwayMs) {
      // Shouldn't normally reach here (ceiling timer disarms first), but guard
      // against timer skew.
      this.disarm();
      return;
    }

    if (elapsedMs < this.minAwayMs) {
      // Too fast to be a human return — a browser auto-dispatched side effect of
      // the navigation. Ignore but STAY ARMED so the genuine later return is
      // still caught (the listeners are not { once }, so they remain attached).
      logger.debug(
        `AccidentalClickDetector: ignoring sub-floor ${signal} at ${elapsedMs}ms (stay armed)`,
      );
      return;
    }

    const callback = this.onAccidental;
    this.fired = true;
    this.disarm();

    if (callback) {
      logger.info(`AccidentalClickDetector: re-engagement via ${signal} after ${elapsedMs}ms`);
      // The callback dispatches into the analytics pipeline. This runs inside a
      // DOM event handler (click/scroll/visibilitychange/pageshow), so a throw
      // would escape as an unhandled exception on the publisher page. Contain it.
      try {
        callback(elapsedMs);
      } catch (e) {
        logger.error(`AccidentalClickDetector callback failed: ${e}`);
      }
    }
  }
}

export { AccidentalClickDetector };
